import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { categoryDef } from '@/lib/constants';
import { formatVNDShort } from '@/lib/format';
import type { ReportSegment } from '@/lib/api';

export interface ReportSegmentsProps {
  segments: ReportSegment[];
  compareSegments?: ReportSegment[];
  compareLabel?: string;
}

const SEGMENT_BG: Record<string, string> = {
  oled: 'bg-cat-oled/5 border-cat-oled/20',
  gaming: 'bg-cat-gaming/5 border-cat-gaming/20',
  graphic: 'bg-cat-graphic/5 border-cat-graphic/20',
  curved: 'bg-cat-curved/5 border-cat-curved/20',
  popular: 'bg-cat-popular/5 border-cat-popular/20',
  portable: 'bg-cat-portable/5 border-cat-portable/20',
};

function delta(current: number, prev: number): number {
  if (prev <= 0) return 0;
  return ((current - prev) / prev) * 100;
}

function MiniDelta({ pct }: { pct: number }) {
  if (Math.abs(pct) < 0.5) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
        <Minus className="h-2.5 w-2.5" />
        ±0%
      </span>
    );
  }
  const Icon = pct > 0 ? TrendingUp : TrendingDown;
  const colorClass = pct > 0 ? 'text-cat-graphic' : 'text-destructive';
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-[10px] font-medium', colorClass)}>
      <Icon className="h-2.5 w-2.5" strokeWidth={2.5} />
      {pct > 0 ? '+' : ''}
      {pct.toFixed(0)}%
    </span>
  );
}

export function ReportSegments({ segments, compareSegments, compareLabel }: ReportSegmentsProps) {
  if (!segments.length) return null;

  const hasCompare = Boolean(compareSegments && compareSegments.length > 0);
  const compareMap = new Map(compareSegments?.map((s) => [s.category, s]) ?? []);

  return (
    <section className="print-no-break">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Phân tích theo Segment
      </h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {segments.map((seg) => {
          const cat = categoryDef(seg.category);
          const Icon = cat?.icon;
          const label = cat?.label ?? seg.category;
          const colorClass = cat?.colorClass ?? 'text-muted-foreground';
          const prev = compareMap.get(seg.category);

          return (
            <div
              key={seg.category}
              className={cn(
                'rounded-lg border p-3 print:rounded-md print:shadow-none',
                SEGMENT_BG[seg.category] ?? 'bg-card'
              )}
            >
              <div className="flex items-center gap-1.5">
                {Icon && <Icon className={cn('h-3.5 w-3.5', colorClass)} />}
                <div className="text-[11px] font-semibold">{label}</div>
              </div>

              <div className="mt-2 text-lg font-bold tabular-nums leading-none">
                {seg.units_sold.toLocaleString('vi-VN')}
              </div>
              <div className="text-[10px] text-muted-foreground">units sold</div>

              {hasCompare && prev && (
                <div className="mt-1.5 flex items-baseline justify-between gap-1 text-[10px]">
                  <span className="tabular-nums text-muted-foreground">
                    {prev.units_sold.toLocaleString('vi-VN')}
                  </span>
                  <MiniDelta pct={delta(seg.units_sold, prev.units_sold)} />
                </div>
              )}

              <div className="mt-2 flex items-baseline justify-between gap-1 border-t pt-1.5">
                <span className="text-[11px] tabular-nums">
                  {formatVNDShort(seg.revenue_vnd)}
                </span>
                <span className="text-[10px] font-semibold tabular-nums text-muted-foreground">
                  {seg.share_pct.toFixed(1)}%
                </span>
              </div>
              <div className="mt-1 text-[10px] text-muted-foreground">
                {seg.sku_count} SKU · {seg.focal_count} focal
              </div>
            </div>
          );
        })}
      </div>
      {hasCompare && compareLabel && (
        <div className="mt-2 text-[10px] text-muted-foreground">
          So sánh với <span className="font-medium">{compareLabel}</span> · units sold + delta
        </div>
      )}
    </section>
  );
}
